import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import { CountDetail } from "../../../../services/v2/stockops.service";
import { apiError, ui } from "../kits/shared";
import { EMPTY_DRAFT, VariantAxes, VariantDraft, draftMissing, newVariantCount, sortSizes, variantFacts } from "../products/variantes";
import MatrizCantidades, { cellKey } from "./matrizCantidades";

/*
	Contar de una vez todas las tallas/colores de un producto que YA existe (Esteban, 2026-10-21: "no las está dejando agregar
	cantidad por talla y/o color de una vez"). Cada casilla suma sus piezas a lo que ya se contó; 0 = se contó y no hay.
	Quien da de alta productos puede agregar ahí mismo la talla o el color que falte.
*/

const ContarPorTallas: React.FC<{
	campusId: number; count: CountDetail; modelId: number; catalogs: { sizeTypes: any; colorSuggestions: string[] }; canCreate: boolean; light?: boolean;
	onDone: (data: any, title: string) => void; onCancel: () => void;
}> = ({ campusId, count, modelId, catalogs, canCreate, light, onDone, onCancel }) => {
	const http = HttpManager.getInstance();
	const [product, setProduct] = useState<any | null>(null);
	const [qty, setQty] = useState<Record<string, string>>({});
	const [draft, setDraft] = useState<VariantDraft>(EMPTY_DRAFT);
	const [adding, setAdding] = useState<boolean>(false);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const load = () => http.inventoryService.get(campusId, modelId)
		.then((response) => setProduct(response.data))
		.catch((requestError) => setError(apiError(requestError, "No se pudo abrir el producto.")));
	useEffect(() => { load(); }, [campusId, modelId]);

	const variants: any[] = (product?.variants || []).filter((variant: any) => !variant.deprecatedAt);
	const scale: string[] = (catalogs.sizeTypes?.[product?.sizeType || "none"]?.values || []).map((entry: any) => String(entry.value));
	const sizes = sortSizes(scale, Array.from(new Set(variants.map((variant) => variant.sizeValue).filter(Boolean) as string[])));
	const colors = Array.from(new Set(variants.map((variant) => variant.colorValue).filter(Boolean) as string[]));
	const byKey = useMemo(() => {
		const map: Record<string, any> = {};
		variants.forEach((variant) => { map[cellKey(variant.colorValue || null, variant.sizeValue || null)] = variant; });
		return map;
	}, [product]);
	const missing = useMemo(() => {
		const out = new Set<string>();
		(sizes.length ? sizes : [null]).forEach((size) => (colors.length ? colors : [null]).forEach((color) => { const key = cellKey(color, size); if (!byKey[key]) out.add(key); }));
		return out;
	}, [byKey]);
	// What this count already has of each size/color: what is typed here adds to it.
	const hints = useMemo(() => {
		const out: Record<string, string> = {};
		Object.entries(byKey).forEach(([key, variant]) => {
			const line = count.lines.find((entry) => entry.articleId === variant.id);
			if (line) out[key] = "ya " + line.counted;
		});
		return out;
	}, [byKey, count.lines]);

	const items = Object.entries(qty).filter(([key, value]) => value.trim() !== "" && byKey[key]).map(([key, value]) => ({ articleId: byKey[key].id as number, quantity: Number(value) }));
	const pieces = items.reduce((sum, item) => sum + item.quantity, 0);

	const save = async () => {
		if (items.length < 1) { setError("Pon cuántas hay en al menos una talla o color."); return; }
		setBusy(true);
		setError(null);
		try {
			const response = await http.stockOpsService.scanMany(campusId, count.id, modelId, items, !!light);
			onDone(response.data, product.name + ": " + (pieces === 1 ? "1 pieza" : pieces + " piezas") + " en " + (items.length === 1 ? "1 talla o color" : items.length + " tallas o colores"));
		} catch (requestError: any) {
			setError(requestError?.response ? apiError(requestError, "No se pudo guardar.") : "Sin señal: no se supo si se guardó. Revisa el conteo antes de volver a guardar.");
		} finally {
			setBusy(false);
		}
	};

	const facts = variantFacts(variants);
	const toCreate = newVariantCount(facts, draft);
	const addMissing = async () => {
		if (toCreate < 1 || draftMissing(facts, draft)) return;
		setBusy(true);
		setError(null);
		try {
			await http.inventoryService.createVariants(campusId, modelId, draft.sizes, draft.colors, draft.existingColor, draft.existingSize);
			setDraft(EMPTY_DRAFT);
			setAdding(false);
			await load();
		} catch (requestError: any) {
			setError(apiError(requestError, "No se pudieron agregar."));
		} finally {
			setBusy(false);
		}
	};

	return <div style={{ position: "fixed", inset: 0, zIndex: 2300, background: "var(--wa-papel)", overflowY: "auto", display: "flex", flexDirection: "column" }}>
		<div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", padding: "10px 14px", background: "var(--wa-superficie)", borderBottom: "1px solid var(--wa-filo)", position: "sticky", top: 0, zIndex: 3 }}>
			<div style={{ minWidth: 0 }}>
				<div style={{ fontWeight: 700, fontSize: "16px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{product ? product.name : "Cargando…"}</div>
				<div style={ui.small}>Contar por Tallas y Colores</div>
			</div>
			<button type="button" className="btn btn-outline-secondary rounded-pill" style={{ flex: "0 0 auto" }} disabled={busy} onClick={onCancel}>Cancelar</button>
		</div>
		<div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: "12px", maxWidth: "720px", width: "100%", margin: "0 auto" }}>
			{product && variants.length < 1 ? <div style={ui.small}>Este producto todavía no tiene tallas ni colores.</div> : null}
			{product && variants.length > 0
				? <MatrizCantidades sizes={sizes} colors={colors} qty={qty} busy={busy} onChange={setQty} hints={hints} missing={missing} /> : null}
			{product && canCreate ? (adding
				? <div style={{ ...ui.card, padding: "10px 12px" }}>
					<VariantAxes catalogs={catalogs} sizeType={product.sizeType || "none"} facts={facts} draft={draft} busy={busy} onChange={setDraft} />
					<div style={{ display: "flex", gap: "8px", justifyContent: "flex-end", marginTop: "8px" }}>
						<button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => { setAdding(false); setDraft(EMPTY_DRAFT); }}>Cancelar</button>
						<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || toCreate < 1 || !!draftMissing(facts, draft)} onClick={addMissing}>
							{toCreate === 1 ? "Agregar 1" : "Agregar " + toCreate}</button>
					</div>
				</div>
				: <button type="button" className="btn btn-outline-secondary rounded-pill" disabled={busy} onClick={() => setAdding(true)}>Falta una Talla o Color</button>) : null}
			{error ? <div className="alert alert-danger" style={{ padding: "8px 12px", fontSize: "14px", margin: 0 }}>{error}</div> : null}
			<button type="button" className="btn btn-primary rounded-pill" style={{ height: "42px", fontSize: "15px" }} disabled={busy || !product || items.length < 1} onClick={save}>
				{busy ? "Guardando…" : items.length < 1 ? "Sumar al Conteo" : "Sumar " + (pieces === 1 ? "1 Pieza" : pieces + " Piezas") + " al Conteo"}</button>
		</div>
	</div>;
};

export default ContarPorTallas;
